// src/pages/AdminBlogFormPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { BlogService } from '../services/blog.service';
import type { BlogCategory } from '../types/Blog';
import styles from './AdminBlogFormPage.module.css';

export default function AdminBlogFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [categoryIds, setCategoryIds] = useState<(string | number)[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      try {
        const cats = await BlogService.getAdminCategories();
        if (!alive) return;
        setCategories(Array.isArray(cats) ? cats : []);

        if (isEdit) {
          const p = await BlogService.getAdminPost(id!);
          if (!alive) return;
          setTitle(p.title || '');
          setExcerpt(p.excerpt || '');
          setContent(p.content || '');
          setCategoryIds(p.categories?.map(c => c.id) || []);
        }
      } catch {
        if (!alive) return;
        setCategories([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [id, isEdit]);

  const selectedValues = useMemo(() => categoryIds.map(String), [categoryIds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setSaving(true);
    const payload = {
      title: title.trim(),
      excerpt: excerpt?.trim() || '',
      content: content || '',
      categoryIds,
      status: 'PUBLISHED'
    };

    try {
      if (isEdit) {
        await BlogService.updatePost(id!, payload);
      } else {
        await BlogService.createPost(payload);
      }
      navigate('/admin/blogs');
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : t('common.unexpected_error') || 'Error desconocido';
      alert((t('admin.blog_save_error') || 'Error al guardar el blog') + ': ' + msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <Link to="/admin/blogs" className={styles.backButton}>
            <FiArrowLeft />
          </Link>

          <div className={styles.headerText}>
            <h1 className={styles.title}>{isEdit ? t('admin.edit_blog') || 'Editar Blog' : t('admin.new_blog') || 'Nuevo Blog'}</h1>
            <p className={styles.subtitle}>{t('admin.blog_form_subtitle') || 'Define contenido, categorías y visibilidad.'}</p>
          </div>
        </div>

        <button
          type="submit"
          form="admin-blog-form"
          className={styles.primaryAction}
          disabled={saving || loading}
        >
          <FiSave className={styles.actionIcon} />
          {saving ? t('common.saving') || 'Guardando...' : t('common.save') || 'Guardar'}
        </button>
      </header>

      <section className={styles.card}>
        {loading ? (
          <div className={styles.loading}>{t('common.loading') || 'Cargando...'}</div>
        ) : (
          <form id="admin-blog-form" onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <label className={styles.label}>{t('admin.blog_title') || 'Título'}</label>
              <input
                className={styles.input}
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t('admin.blog_title_placeholder') || 'Título'}
                required
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{t('admin.blog_excerpt') || 'Resumen'}</label>
              <textarea
                className={styles.textarea}
                value={excerpt}
                onChange={e => setExcerpt(e.target.value)}
                placeholder={t('admin.blog_excerpt_placeholder') || 'Resumen'}
                rows={3}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{t('admin.blog_content') || 'Contenido (HTML)'}</label>
              <textarea
                className={styles.textarea}
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder={t('admin.blog_content_placeholder') || 'Contenido HTML'}
                rows={12}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{t('admin.blog_categories') || 'Categorías'}</label>
              <select
                className={styles.select}
                multiple
                value={selectedValues}
                onChange={e => {
                  const vals = Array.from(e.target.selectedOptions).map(o => o.value);
                  setCategoryIds(vals);
                }}
              >
                {categories.map(c => (
                  <option key={String(c.id)} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </select>
              <div className={styles.helper}>
                {t('admin.multi_select_hint') || 'Ctrl/Cmd para seleccionar múltiples.'}
              </div>
            </div>

            <div className={styles.footer}>
              <Link to="/admin/blogs" className={styles.secondaryAction}>
                {t('common.cancel') || 'Cancelar'}
              </Link>

              <button type="submit" className={styles.primaryActionInline} disabled={saving}>
                <FiSave className={styles.actionIcon} />
                {saving ? t('common.saving') || 'Guardando...' : t('common.save') || 'Guardar'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
